/**
 * scene-backdrop — the illustrated full-bleed meadow of the home.
 *
 * Gradient sky, smiling sun with slowly spinning rays, drifting clouds and
 * rolling hills: the "ground" where the toy-objects live.
 * 100% decorative (aria-hidden, pointer-events-none). The animations are
 * slow, GPU-only and killed by the global reduced-motion kill-switch.
 */

import type { JSX } from "solid-js";

export function SceneBackdrop(): JSX.Element {
  return (
    <div aria-hidden="true" class="pointer-events-none absolute inset-0 overflow-hidden">
      {/* Cielo */}
      <div
        class="absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, oklch(0.93 0.05 230), oklch(0.965 0.03 200) 55%, var(--joy-cream) 100%)",
        }}
      />

      {/* Sol sonriente con rayos girando */}
      <svg class="absolute -top-6 -right-6 h-40 w-40 sm:h-52 sm:w-52" viewBox="0 0 120 120">
        <g class="toy-sun-rays" style={{ "transform-origin": "60px 60px" }}>
          <g fill="var(--joy-sunshine)" opacity="0.85">
            <path d="M60 6 l6 18 h-12 z" />
            <path d="M60 114 l6 -18 h-12 z" />
            <path d="M6 60 l18 -6 v12 z" />
            <path d="M114 60 l-18 -6 v12 z" />
            <path d="M22 22 l16 9 l-8 8 z" />
            <path d="M98 98 l-16 -9 l8 -8 z" />
            <path d="M98 22 l-9 16 l-8 -8 z" />
            <path d="M22 98 l9 -16 l8 8 z" />
          </g>
        </g>
        <circle
          cx="60"
          cy="60"
          r="26"
          fill="var(--joy-sunshine)"
          stroke="oklch(0.78 0.16 60)"
          stroke-width="2"
        />
        <circle cx="52" cy="56" r="2.6" fill="oklch(0.3 0.045 40)" />
        <circle cx="68" cy="56" r="2.6" fill="oklch(0.3 0.045 40)" />
        <path
          d="M52 66 q8 7 16 0"
          fill="none"
          stroke="oklch(0.3 0.045 40)"
          stroke-width="2.4"
          stroke-linecap="round"
        />
      </svg>

      {/* Nubes derivando */}
      <svg
        class="toy-cloud absolute top-[8%] left-[-18%] h-12 w-32 opacity-90"
        viewBox="0 0 120 48"
      >
        <g fill="oklch(1 0 0 / 0.9)">
          <ellipse cx="36" cy="32" rx="30" ry="14" />
          <circle cx="30" cy="20" r="13" />
          <circle cx="52" cy="18" r="15" />
          <circle cx="74" cy="26" r="12" />
        </g>
      </svg>
      <svg
        class="toy-cloud toy-cloud-slow absolute top-[22%] left-[-30%] h-9 w-24 opacity-70"
        viewBox="0 0 120 48"
      >
        <g fill="oklch(1 0 0 / 0.85)">
          <ellipse cx="40" cy="32" rx="32" ry="13" />
          <circle cx="34" cy="20" r="12" />
          <circle cx="58" cy="18" r="14" />
        </g>
      </svg>

      {/* Colinas del fondo */}
      <svg
        class="absolute bottom-0 left-0 h-[46%] w-full"
        viewBox="0 0 400 160"
        preserveAspectRatio="none"
      >
        <path d="M0 90 Q 80 40 170 78 T 400 70 V160 H0 Z" fill="oklch(0.86 0.09 150)" />
        <path d="M0 120 Q 110 70 220 108 T 400 104 V160 H0 Z" fill="oklch(0.8 0.11 145)" />
        <path d="M0 150 Q 130 108 260 138 T 400 132 V160 H0 Z" fill="oklch(0.74 0.13 142)" />
        {/* Florecitas */}
        <g fill="var(--joy-bubblegum)">
          <circle cx="46" cy="132" r="3" />
          <circle cx="330" cy="126" r="3" />
        </g>
        <g fill="var(--joy-sunshine)">
          <circle cx="120" cy="142" r="2.6" />
          <circle cx="255" cy="148" r="2.6" />
        </g>
      </svg>
    </div>
  );
}
